import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Store, select } from '@ngrx/store'
import { AccountObject, AccountProductSpec, FileObjectAttributes, Product } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { find, groupBy, isEqual } from 'lodash-es'
import { BehaviorSubject, ObservedValueOf, combineLatest } from 'rxjs'
import { map, switchMap, take } from 'rxjs/operators'
import { AuthApiService } from 'src/api/auth'
import { FileApiService } from 'src/api/file'
import { loadItemTypes, selectAllItemTypes } from 'src/app/store/item-types'
import { loadProductCategories, selectAllProductCategories } from 'src/app/store/product-categories'
import { loadProductTypes, selectAllProductTypes } from 'src/app/store/product-types'
import { loadProducts, selectAllProducts, selectProductEntities } from 'src/app/store/products'
import { selectCompanyUsers } from 'src/app/store/users'
import { AccountsService } from 'src/services/data/accounts.service'
import { FileInfo, ProductAttachmentsService } from 'src/services/data/product-attachments.service'
import { isAtLeastAdmin } from 'src/services/data/users.service'
import { ModalService } from 'src/shared/modal'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { replayForm } from 'src/shared/utils/replay-form'
import { waitNotEmpty } from 'src/shared/utils/wait-not-empty'
import {
  ProductAttachmentsDialogComponent,
  ProductAttachmentsDialogOptions,
  ProductAttachmentsDialogResult,
} from './product-attachments-dialog/product-attachments-dialog.component'

// the parts of a product override row used by the attachment helpers
interface ProductOverrideRow {
  itemTypeId: string
  product: DeepReadonly<Product>
  form: FormGroup<any>
}

// attachment fields per product spec row, keyed by `${product_id}:${item_type_id}`.
// removedFileIds and archiveToggles are applied only after the account save succeeds
type ProductAttachmentFields = Map<string, Pick<AccountProductSpec, 'attachment_file_ids' | 'product_pic_file_id' | 'tech_spec_file_id'> & {
  removedFileIds: string[]
  archiveToggles: FileInfo[]
}>

export interface CompanyProfileFormOptions {
  title: string
  isBulkEdit: boolean
  company: DeepReadonly<AccountObject>
  selectedCompanies: DeepReadonly<AccountObject[]>
  defaults: {
    businessTypeId: string
    categoryId: string
    typeId: string
    itemTypeId: string
    contactId: string
    products_spec: DeepReadonly<AccountProductSpec[]>
  }
}

@Component({
  selector: 'tc-company-profile-form',
  styleUrl: './company-profile-form.component.scss',
  templateUrl: './company-profile-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CompanyProfileFormComponent implements OnInit {
  private readonly toaster = inject(ToasterService)
  private readonly AuthApi = inject(AuthApiService)
  private readonly Accounts = inject(AccountsService)
  private readonly FileApi = inject(FileApiService)
  private readonly ProductAttachments = inject(ProductAttachmentsService)
  private readonly store = inject(Store)
  private readonly cd = inject(ChangeDetectorRef)
  private readonly modal = inject(ModalService)
  private readonly dialogRef = inject<MatDialogRef<CompanyProfileFormComponent, DeepReadonly<AccountObject | AccountObject[]>>>(MatDialogRef)
  private readonly dialogData = inject<CompanyProfileFormOptions>(MAT_DIALOG_DATA)


  // options
  protected readonly title = this.dialogData.title
  private readonly isNew = this.dialogData.company.account
  protected readonly isBulkEdit = this.dialogData.isBulkEdit
  protected readonly selectedCompanies = this.dialogData.selectedCompanies
  protected readonly company = this.dialogData.isBulkEdit ? this.dialogData.selectedCompanies[0] : this.dialogData.company
  protected readonly enablement = ['manager', 'administrator', 'junior-administrator', 'superuser'].includes(this.AuthApi.currentUser.role)
    && (this.company.type === 'supplier' || this.company.type === 'buyer')
  protected readonly enablementLabel = {
    'buyer': 'Available for SO matching',
    'supplier': 'Enable for SO creation',
  }[this.company.type] || ''
  protected readonly isBuyer = this.company.type === 'buyer'
  protected readonly isAtLeastAdminUser = isAtLeastAdmin(this.AuthApi.currentUser)
  // product attachments (WA-22050) are stored per supplier+product pair
  protected readonly canEditAttachments = !this.isBulkEdit && this.company.type === 'supplier'

  // form
  protected readonly form = new FormGroup({
    businessTypeId: new FormControl(this.dialogData.defaults.businessTypeId, Validators.required),
    itemTypeId: new FormControl(this.dialogData.defaults.itemTypeId),
    categoryId: new FormControl(this.dialogData.defaults.categoryId, Validators.required),
    typeId: new FormControl(this.dialogData.defaults.typeId, Validators.required),
    contactId: new FormControl(this.dialogData.defaults.contactId),
    productIds: new FormControl(
      this.dialogData.defaults.products_spec.map(ps => ps.product_id),
      Validators.required),
  })
  private readonly productOverrides = new Map<string, ReturnType<typeof this.newOverrideForm>>()
  // attachment changes staged in the dialog, persisted by save(). keyed by `${product_id}:${item_type_id}`
  private readonly stagedAttachments = new Map<string, FileInfo[]>()
  protected productNameOverrides$ = combineLatest([
    replayForm(this.form.controls.itemTypeId),
    replayForm(this.form.controls.productIds),
    replayForm(this.form.controls.contactId),
    this.store.pipe(select(selectProductEntities), waitNotEmpty()),
  ]).pipe(map(([itemTypeId, productIds, contactId, productsById]) =>
    productIds.map(productId => {
      const product = productsById[productId]
      const standardHsCode = this.getStandardHsCode(product, itemTypeId)
      return {
        itemTypeId,
        contactId,
        product,
        standardHsCode,
        form: this.getOverrideForm(productId, itemTypeId, this.company),
      }
    })))

  // ref data
  private readonly categories$ = this.store.pipe(
    select(selectAllProductCategories),
    waitNotEmpty(),
    map(categories => groupBy(categories, item => (item.type || 'unknown').toLowerCase())))
  private readonly types$ = this.store.pipe(select(selectAllProductTypes), waitNotEmpty(), map(types => groupBy(types, 'category_id')))
  private readonly products$ = this.store.pipe(select(selectAllProducts), waitNotEmpty(), map(products => groupBy(products, 'type_id')))

  protected readonly contacts$ = replayForm(this.form.controls.contactId).pipe(switchMap(selected =>
    this.store.pipe(select(selectCompanyUsers(this.company.account, selected)), waitNotEmpty())))
  protected readonly itemTypes$ = this.store.pipe(select(selectAllItemTypes), waitNotEmpty())
  protected readonly businessTypes$ = this.categories$.pipe(map(categories => Object.keys(categories)))
  protected readonly limitedCategories$ = combineLatest([replayForm(this.form.controls.businessTypeId), this.categories$])
    .pipe(map(([businessTypeId, categories]) => categories[businessTypeId] || []))
  protected readonly limitedTypes$ = combineLatest([replayForm(this.form.controls.categoryId), this.types$])
    .pipe(map(([categoryId, types]) => types[categoryId] || []))
  protected readonly limitedProducts$ = combineLatest([replayForm(this.form.controls.typeId), this.products$])
    .pipe(map(([typeId, products]) => products[typeId] || []))

  // state
  protected readonly inProgress$ = new BehaviorSubject<'loading'|'save'|undefined>('loading')

  ngOnInit() {
    this.store.dispatch(loadProductCategories())
    this.store.dispatch(loadProductTypes())
    this.store.dispatch(loadProducts())
    this.store.dispatch(loadItemTypes())
    combineLatest([this.categories$, this.types$, this.products$, this.itemTypes$])
      .pipe(take(1)).subscribe(() => this.inProgress$.next(undefined))
  }

  protected onBusinessTypeChanged() {
    this.form.patchValue({ categoryId: '', typeId: '', productIds: [] })
  }

  protected onCategoryChanged() {
    this.form.patchValue({ typeId: '', productIds: [] })
  }

  protected onTypeChanged() {
    this.form.patchValue({ productIds: [] })
  }

  protected editAttachments(row: ProductOverrideRow) {
    const key = `${row.product.product_id}:${row.itemTypeId}`
    const spec = this.findProductSpec(this.company, row.product.product_id, row.itemTypeId)
    this.modal.openLargeSide<ProductAttachmentsDialogComponent, ProductAttachmentsDialogOptions, ProductAttachmentsDialogResult>(
      ProductAttachmentsDialogComponent, {
        productName: row.form.controls.name.value || row.product.name,
        fileIds: (spec?.attachment_file_ids || []) as string[],
        productPicFileId: spec?.product_pic_file_id,
        techSpecFileId: spec?.tech_spec_file_id,
        stagedFiles: this.stagedAttachments.get(key),
      }, { width: '96%', maxWidth: '96vw' }).subscribe(result => {
        if (!result) return
        this.stagedAttachments.set(key, result.files)
        this.cd.markForCheck()
      })
  }

  protected hasAttachments(row: ProductOverrideRow) {
    const staged = this.stagedAttachments.get(`${row.product.product_id}:${row.itemTypeId}`)
    if (staged) return staged.length > 0
    return !!this.findProductSpec(this.company, row.product.product_id, row.itemTypeId)?.attachment_file_ids?.length
  }

  protected removeProductOverride(row: any) {
    const currentProductIds = this.form.controls.productIds.value || []
    const updatedProductIds = currentProductIds.filter(id => id !== row.product.product_id)
    this.form.patchValue({ productIds: updatedProductIds })
    
    // Clean up the override form from the Map
    const key = `${row.product.product_id}:${row.itemTypeId}`
    this.productOverrides.delete(key)
  }

  protected save() {
    if (this.inProgress$.value) return
    this.form.markAllAsTouched()
    this.form.updateValueAndValidity()
    this.productOverrides.forEach(form => form.markAllAsTouched())
    if (!this.form.valid) return

    this.inProgress$.next('save')
    this.productNameOverrides$.pipe(take(1)).subscribe(async productNameOverrides => {
      try {
        let productAttachments: ProductAttachmentFields
        try {
          productAttachments = await this.saveAttachments(productNameOverrides)
        } catch (err) {
          console.error('Unable to save product attachments.', err)
          this.toaster.error('Unable to save product attachments.', err)
          return
        }

        if (this.isBulkEdit) {
          const companies = await this.saveForMultipleCompanies(productNameOverrides, productAttachments)
          this.toaster.success('Companies Profiles updated successfully')
          this.dialogRef.close(companies)
        } else {
          const company = await this.saveForSingleCompany(this.company, productNameOverrides, productAttachments)
          if (company) {
            // only once the spec is saved — a failed save must leave the files' archive state untouched.
            // on failure the dialog stays open with the failed toggles still staged, so Save retries them
            if (!await this.applyArchiveChanges(productAttachments)) return
            if (!this.isNew) this.toaster.success('Company Profile updated successfully')
            this.dialogRef.close(company)
          }
        }
      } finally {
        this.inProgress$.next(undefined)
      }
    })
  }

  /**
   * Persist staged attachment changes (WA-22050): upload new files and update changed
   * descriptions/suffixes. Returns the attachment fields to store on each product spec
   * row, keyed by `${product_id}:${item_type_id}`, along with the removed file ids and the
   * staged archive/restore toggles — applyArchiveChanges() persists those once the account
   * save succeeds. Archived files stay in attachment_file_ids so they can be restored (WA-22618).
   */
  private async saveAttachments(productNameOverrides: ObservedValueOf<typeof this.productNameOverrides$>) {
    const results: ProductAttachmentFields = new Map()
    for (const row of productNameOverrides) {
      const key = `${row.product.product_id}:${row.itemTypeId}`
      const staged = this.stagedAttachments.get(key)
      if (!staged) continue

      const attachment_file_ids = await this.ProductAttachments.persistFileInfos(staged, {
        uploadPayload: fileInfo => ({
          visibility: 1,
          description: fileInfo.description || undefined,
          attributes: this.getFileAttributes(row, fileInfo),
        }),
        fileAttributes: fileInfo => this.getFileAttributes(row, fileInfo),
      })

      // collect files removed from this product; they are archived only after the account
      // save succeeds, and never hard-deleted — offers/deals may reference them
      const spec = this.findProductSpec(this.company, row.product.product_id, row.itemTypeId)
      const removedFileIds = (spec?.attachment_file_ids || []).filter(id => !attachment_file_ids.includes(id))

      results.set(key, {
        attachment_file_ids,
        product_pic_file_id: this.ProductAttachments.getProductPicFileId(staged),
        tech_spec_file_id: this.ProductAttachments.getTechSpecFileId(staged),
        removedFileIds,
        archiveToggles: staged.filter(f => f.archivedChanged),
      })
    }
    return results
  }

  /** False when any archive/restore request failed; succeeded toggles are cleared, failed ones stay staged. */
  private async applyArchiveChanges(productAttachments: ProductAttachmentFields): Promise<boolean> {
    const attachments = [...productAttachments.values()]
    const requests = [
      ...attachments.flatMap(a => a.removedFileIds).map(id => this.FileApi.archive(id)),
      ...attachments.flatMap(a => a.archiveToggles).map(async fileInfo => {
        await (fileInfo.archived ? this.FileApi.archive(fileInfo.file_id) : this.FileApi.unarchive(fileInfo.file_id))
        fileInfo.archivedChanged = false
      }),
    ]
    const failures = (await Promise.allSettled(requests)).filter(r => r.status === 'rejected')
    if (!failures.length) return true
    console.error('Unable to archive or restore attachments.', failures.map(f => (f as PromiseRejectedResult).reason))
    this.toaster.error('The profile was saved, but some attachments could not be archived or restored. Save again to retry.')
    return false
  }

  private getFileAttributes(row: ProductOverrideRow, fileInfo: FileInfo): FileObjectAttributes {
    return {
      supplier_account: this.company.account,
      product_id: row.product.product_id,
      product_type_id: row.product.type_id,
      product_category_id: row.product.category_id,
      item_type_id: row.itemTypeId || undefined,
      suffix: fileInfo.suffix || undefined,
    }
  }

  private findProductSpec(company: DeepReadonly<AccountObject>, productId: string, itemTypeId: string) {
    return company.products_spec?.find(pc =>
      pc.product_id === productId && (pc.item_type_id === itemTypeId || !pc.item_type_id && !itemTypeId))
  }

  private async saveForSingleCompany(
    company: DeepReadonly<AccountObject>,
    productNameOverrides: ObservedValueOf<typeof this.productNameOverrides$>,
    productAttachments: ProductAttachmentFields,
  ) {
    try {
      const otherProducts = (company.products_spec || []).filter(x =>
        !productNameOverrides.some(y =>
          x.product_id === y.product.product_id && (x.item_type_id === y.itemTypeId || !x.item_type_id && !y.itemTypeId)) &&
        !this.dialogData.defaults.products_spec.some(y => isEqual(x, y)))
      const formProducts = productNameOverrides.map(({ itemTypeId, contactId, product, form }) => {
        // attachment fields are not editable in this form directly — take the staged dialog
        // result, or carry the company's saved values through so a profile save keeps them
        const existingSpec = this.findProductSpec(company, product.product_id, itemTypeId)
        const attachments = productAttachments.get(`${product.product_id}:${itemTypeId}`) ?? {
          attachment_file_ids: existingSpec?.attachment_file_ids as string[],
          product_pic_file_id: existingSpec?.product_pic_file_id,
          // without this a profile save that never opened the dialog would wipe the tech spec
          tech_spec_file_id: existingSpec?.tech_spec_file_id,
        }
        return {
          product_id: product.product_id,
          item_type_id: itemTypeId || undefined,
          name: form.controls.name.value || undefined,
          hs_code: form.controls.hs_code.value || undefined,
          default_margin: form.controls.default_margin.value || undefined,
          offer_creation: form.controls.offer_creation.value || undefined,
          contact_id: contactId || undefined,
          attachment_file_ids: attachments.attachment_file_ids?.length ? attachments.attachment_file_ids : undefined,
          product_pic_file_id: attachments.product_pic_file_id || undefined,
          tech_spec_file_id: attachments.tech_spec_file_id || undefined,
        }
      })
      const products_spec = [...otherProducts, ...formProducts]
      const products = products_spec.map(spec => spec.product_id)
      if (company.account) {
        company = await this.Accounts.updateAccountProducts(company, products_spec)
      } else {
        company = { ...company, products, products_spec }
      }
      return company
    } catch (err) {
      console.error('Unable to save product name overrides.', err)
      // PUT /accounts/:account returns the code as error.subtypeCode (WA-22611)
      if ((err as any)?.error?.error?.subtypeCode === 'TECH_SPEC_IS_PRODUCT_PIC') {
        this.toaster.error('An attachment cannot be both the Product Pic and the Tech Spec.')
      } else {
        this.toaster.error('Unable to save product name overrides.', err)
      }
      return undefined
    }
  }

  private saveForMultipleCompanies(
    productNameOverrides: ObservedValueOf<typeof this.productNameOverrides$>,
    productAttachments: ProductAttachmentFields,
  ) {
    return Promise.all(this.selectedCompanies.map(company =>
      this.saveForSingleCompany(company, productNameOverrides, productAttachments)))
  }

  protected getProductEnablement(row) {
    return { color: row.product.so_creation ? 'lightgreen' : 'red' }
  }

  private getOverrideForm(productId: string, itemTypeId: string, company: DeepReadonly<AccountObject>) {
    const key = `${productId}:${itemTypeId}`
    if (this.productOverrides.has(key)) return this.productOverrides.get(key)
    const override = company.products_spec?.find(pc =>
      pc.product_id === productId && (pc.item_type_id === itemTypeId || !pc.item_type_id && !itemTypeId))
    let form = this.newOverrideForm(override)
    this.productOverrides.set(key, form)
    return form
  }

  private newOverrideForm(override?: DeepReadonly<AccountProductSpec>) {
    return new FormGroup({
      name: new FormControl(override?.name),
      hs_code: new FormControl(override?.hs_code),
      default_margin: new FormControl(override?.default_margin),
      offer_creation: new FormControl(override?.offer_creation),
    })
  }

  private getStandardHsCode(product: any, itemTypeId?: string): string {
    if (!product?.standard_hs_codes) return ''    
    const matchingHsCode = find(product.standard_hs_codes, hsCode => hsCode.item_type_id === itemTypeId)
    return matchingHsCode?.hs_code || ''
  }
}
